import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material';
import { ViewKanban as L2Icon, Save as SaveIcon } from '@mui/icons-material';
import { authGet, authPut } from '../../services/apiService';
import InvestigationSyncAdmin from './InvestigationSyncAdmin';

const CONFIG_URL = '/api/customer-issues/vital-signs/l2-open-config';
const PRIORITIES = [1, 2, 3, 4];
const SCOPE_OPTIONS = [
  {
    value: 'all',
    label: 'All open',
    help: 'The Open number counts every open L2 ticket, whether or not an L1 (Salesforce) ticket links to it.'
  },
  {
    value: 'linkedOpenL1',
    label: 'Linked to an open L1 ticket',
    help: [
      'The Open number counts only L2 tickets that an open Salesforce case links to.',
      'The list on the L2 Open page still shows every open L2 ticket; it just starts filtered to the linked ones.'
    ].join(' ')
  }
];

/**
 * Admin: what the L2 Open number on Vital Signs counts. The same rule drives the L2 Open tile, the Open number
 * in the L2 Open box and the list below it.
 */
const L2OpenConfigAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [availableStates, setAvailableStates] = useState([]);
  const [saved, setSaved] = useState(null);
  const [scope, setScope] = useState('linkedOpenL1');
  const [excludedStates, setExcludedStates] = useState([]);
  const [priorities, setPriorities] = useState(PRIORITIES);
  const [summary, setSummary] = useState({ description: '', openCount: null, updatedAt: null });

  const applyConfig = useCallback((config) => {
    setSaved(config);
    setScope(config.scope);
    setExcludedStates(config.excludedStates);
    setPriorities(config.priorities);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authGet(CONFIG_URL, null, 'json', { useCache: false });
      if (!response.success) throw new Error(response.message || 'Failed to load the L2 Open configuration');
      applyConfig(response.config);
      setAvailableStates(response.availableStates || []);
      setSummary({ description: response.description, openCount: response.openCount, updatedAt: response.updatedAt });
    } catch (error) {
      setMessage({ severity: 'error', text: error.message || 'Failed to load the L2 Open configuration' });
    } finally {
      setLoading(false);
    }
  }, [applyConfig]);

  useEffect(() => { load(); }, [load]);

  // States already excluded but no longer present locally stay in the list so an admin can see and clear them.
  const stateRows = useMemo(() => {
    const known = new Set(availableStates.map((row) => row.state));
    return [
      ...availableStates,
      ...excludedStates.filter((state) => !known.has(state)).map((state) => ({ state, count: 0 }))
    ];
  }, [availableStates, excludedStates]);

  const dirty = saved != null && (
    saved.scope !== scope
    || JSON.stringify([...saved.excludedStates].sort()) !== JSON.stringify([...excludedStates].sort())
    || JSON.stringify([...saved.priorities].sort()) !== JSON.stringify([...priorities].sort())
  );

  const toggleState = (state) => {
    setExcludedStates((prev) => (prev.includes(state) ? prev.filter((item) => item !== state) : [...prev, state]));
  };
  const togglePriority = (priority) => {
    setPriorities((prev) => (prev.includes(priority) ? prev.filter((item) => item !== priority) : [...prev, priority]));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const response = await authPut(CONFIG_URL, { scope, excludedStates, priorities });
      if (!response.success) throw new Error(response.message || 'Failed to save the L2 Open configuration');
      applyConfig(response.config);
      setSummary({ description: response.description, openCount: response.openCount, updatedAt: response.updatedAt });
      setMessage({ severity: 'success', text: `Saved. The L2 Open number is now ${response.openCount}.` });
    } catch (error) {
      setMessage({ severity: 'error', text: error.message || 'Failed to save the L2 Open configuration' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>;
  }

  return (
    <>
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <L2Icon sx={{ color: '#2E7D32' }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>L2 Open definition</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Defines what the L2 Open number on Vital Signs counts. The L2 Open tile and the Open number in the L2 Open
        box use this rule. The ticket list under the box always shows every open L2 ticket (the states from this
        rule); the scope and priorities only set the Open number and the list's starting filters. An L2 ticket is an
        ADO Investigation in the TSI Technical Support Investigation area.
      </Typography>

      {message && <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

      <FormControl sx={{ mb: 3, display: 'block' }}>
        <FormLabel sx={{ fontWeight: 600 }}>Scope</FormLabel>
        <RadioGroup value={scope} onChange={(event) => setScope(event.target.value)}>
          {SCOPE_OPTIONS.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={<Radio size="small" />}
              label={(
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{option.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{option.help}</Typography>
                </Box>
              )}
              sx={{ alignItems: 'flex-start', mb: 0.5 }}
            />
          ))}
        </RadioGroup>
      </FormControl>

      <FormControl sx={{ mb: 3, display: 'block' }}>
        <FormLabel sx={{ fontWeight: 600 }}>States that count as open</FormLabel>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Untick a state to leave its tickets out. New ADO states count as open until you untick them.
        </Typography>
        <FormGroup row>
          {stateRows.map((row) => (
            <FormControlLabel
              key={row.state}
              control={(
                <Checkbox
                  size="small"
                  checked={!excludedStates.includes(row.state)}
                  onChange={() => toggleState(row.state)}
                />
              )}
              label={`${row.state} (${row.count})`}
              sx={{ minWidth: 220 }}
            />
          ))}
        </FormGroup>
      </FormControl>

      <FormControl sx={{ mb: 3, display: 'block' }}>
        <FormLabel sx={{ fontWeight: 600 }}>Priorities included</FormLabel>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          The Open number counts only these priorities. The list on the L2 Open page still shows every priority;
          it starts filtered to these. A ticket with no priority is stored as P3, so P3 also covers tickets with
          no priority.
        </Typography>
        <FormGroup row>
          {PRIORITIES.map((priority) => (
            <FormControlLabel
              key={priority}
              control={<Checkbox size="small" checked={priorities.includes(priority)} onChange={() => togglePriority(priority)} />}
              label={`P${priority}`}
            />
          ))}
        </FormGroup>
        {priorities.length === 0 && <Alert severity="warning" sx={{ mt: 1 }}>Select at least one priority.</Alert>}
      </FormControl>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || !dirty || priorities.length === 0}
        >
          Save
        </Button>
        {summary.updatedAt && (
          <Typography variant="caption" color="text.secondary">
            Last saved {new Date(summary.updatedAt).toLocaleString()}
          </Typography>
        )}
      </Box>

      <Alert severity="info" sx={{ mt: 3 }}>
        <strong>Active rule (saved):</strong> {summary.description}
        {summary.openCount != null && <> It counts <strong>{summary.openCount}</strong> tickets now.</>}
      </Alert>
    </Paper>
    <InvestigationSyncAdmin />
    </>
  );
};

export default L2OpenConfigAdmin;
